import { useState, useMemo, useEffect } from 'react';
import { useTasks } from '../../../contexts/tasks/TasksContext';
import { Task } from '../../../types';
import { useBulkSelection } from '../../../hooks/useBulkSelection';
import { notify } from '../../../utils/toast';
import { useTaskManagement } from './useTaskManagement';
import { TaskChip, filterTasksByChip, computeTaskKpis, groupTasksByBucket, taskDueInRange } from '../lib/taskBuckets';
import type { DateRange } from '../../../components/shared/filters/DateRangePicker';

const PAGE_SIZE = 10;
const VALID_CHIPS: TaskChip[] = ['all', 'overdue', 'today', 'week', 'mine', 'done'];
const TASKS_OPEN_EVENT = 'adlitics:tasks-open';

const searchParams = () => new URLSearchParams(window.location.search);

const chipFromSearch = (params: URLSearchParams): TaskChip => {
    const value = params.get('chip');
    return value && (VALID_CHIPS as string[]).includes(value) ? (value as TaskChip) : 'all';
};

const replaceSearch = (params: URLSearchParams) => {
    const query = params.toString();
    const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    if (`${window.location.pathname}${window.location.search}` === next) return;
    window.history.replaceState(null, '', next);
};

const writeParam = (params: URLSearchParams, key: string, value: string | null) => {
    if (value) params.set(key, value);
    else params.delete(key);
};

export const useTasksPage = (currentUserId?: string | null) => {
    const { tasks: allTasks, isLoading: loading, deleteTask: deleteApi } = useTasks();
    const { saveTask, deleteTask, toggleTaskCompletion, duplicateTask } = useTaskManagement(allTasks);

    const [openId, setOpenId] = useState<string | null>(() => searchParams().get('open'));
    const [searchTerm, setSearchTerm] = useState(() => searchParams().get('q') || '');
    const [assignedToFilter, setAssignedToFilter] = useState<string[]>(
        () => searchParams().get('agents')?.split(',').filter(Boolean) || []
    );
    const [chip, setChip] = useState<TaskChip>(() => chipFromSearch(searchParams()));
    const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    useEffect(() => {
        const params = searchParams();
        writeParam(params, 'q', searchTerm || null);
        writeParam(params, 'agents', assignedToFilter.length ? assignedToFilter.join(',') : null);
        writeParam(params, 'chip', chip === 'all' ? null : chip);
        replaceSearch(params);
    }, [searchTerm, assignedToFilter, chip]);

    useEffect(() => {
        const onOpen = (event: Event) => {
            const taskId = (event as CustomEvent<{ taskId: string }>).detail?.taskId;
            if (taskId) setOpenId(taskId);
        };
        const onPop = () => {
            const id = searchParams().get('open');
            if (id) setOpenId(id);
        };
        window.addEventListener(TASKS_OPEN_EVENT, onOpen);
        window.addEventListener('popstate', onPop);
        return () => {
            window.removeEventListener(TASKS_OPEN_EVENT, onOpen);
            window.removeEventListener('popstate', onPop);
        };
    }, []);

    useEffect(() => {
        if (!openId || loading) return;
        const task = allTasks.find(t => t.id === openId);
        if (!task) return;
        setTaskToEdit(task);
        setIsFormModalOpen(true);
        setOpenId(null);
        const params = searchParams();
        params.delete('open');
        replaceSearch(params);
    }, [openId, loading, allTasks]);

    const baseFiltered = useMemo(() => {
        const q = searchTerm.toLowerCase();
        return allTasks.filter(task => {
            const matchesSearch =
                task.title.toLowerCase().includes(q) ||
                (task.description?.toLowerCase().includes(q) ?? false);
            const matchesAssigned =
                assignedToFilter.length === 0 ||
                (task.assignedTo?.id != null && assignedToFilter.includes(task.assignedTo.id));
            return matchesSearch && matchesAssigned && taskDueInRange(task.dueDate, dateRange.start, dateRange.end);
        });
    }, [allTasks, searchTerm, assignedToFilter, dateRange]);

    const kpis = useMemo(() => computeTaskKpis(baseFiltered), [baseFiltered]);
    const chipFiltered = useMemo(
        () => filterTasksByChip(baseFiltered, chip, currentUserId),
        [baseFiltered, chip, currentUserId],
    );
    const chipCounts = useMemo(() => ({
        mine: filterTasksByChip(baseFiltered, 'mine', currentUserId).length,
    }), [baseFiltered, currentUserId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, assignedToFilter, chip, dateRange]);

    const totalCount = chipFiltered.length;
    const pageTasks = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return chipFiltered.slice(start, start + PAGE_SIZE);
    }, [chipFiltered, currentPage]);
    const groups = useMemo(() => groupTasksByBucket(pageTasks), [pageTasks]);
    const { selectedIds, toggleSelection, clearSelection } = useBulkSelection(pageTasks);

    const openForm = (task?: Task) => {
        setTaskToEdit(task);
        setIsFormModalOpen(true);
    };

    const handleSaveTask = async (taskData: Partial<Task>) => {
        const success = await saveTask(taskData, taskToEdit?.id);
        if (success) setIsFormModalOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!taskToDelete) return;
        await deleteTask(taskToDelete.id);
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
    };

    const handleToggleCompletion = async (taskId: string) => {
        const task = allTasks.find(t => t.id === taskId);
        if (task) await toggleTaskCompletion(task);
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        try {
            for (const id of ids) await deleteApi(id);
            notify.success(`${ids.length} tarefas excluídas.`);
            setIsBulkDeleteModalOpen(false);
            clearSelection();
        } catch {
            notify.error('Erro ao excluir tarefas');
        }
    };

    return {
        groups,
        kpis,
        chipCounts,
        pendingCount: kpis.pending,
        chip, setChip,
        dateRange, setDateRange,
        totalCount,
        currentPage, setCurrentPage,
        pageSize: PAGE_SIZE,
        loading,
        searchTerm, setSearchTerm,
        assignedToFilter, setAssignedToFilter,
        selectedIds, toggleSelection, clearSelection,
        isFormModalOpen, setIsFormModalOpen, taskToEdit,
        isDeleteModalOpen, setIsDeleteModalOpen, taskToDelete,
        isBulkDeleteModalOpen, setIsBulkDeleteModalOpen,
        handleOpenCreateModal: () => openForm(),
        handleOpenEditModal: openForm,
        handleOpenDeleteModal: (task: Task) => {
            setTaskToDelete(task);
            setIsDeleteModalOpen(true);
        },
        handleSaveTask,
        handleConfirmDelete,
        handleDuplicate: duplicateTask,
        handleToggleCompletion,
        handleBulkDelete,
    };
};
